import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { loginUser, verifyTwoFactor } from '../redux/authSlice';
import { showErrorMessage } from '@/shared/components/toast-system';
import { getUserReadableError } from '../../utils/utils';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const [is2FA, setIs2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [tempUserId, setTempUserId] = useState(null);

  const handleAuthResponse = (response) => {
    if (response?.action === 'verify_2fa') {
      setVerificationCode('');
      setIs2FA(true);
      setTempUserId(response.userId);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(loginUser(credentials, navigate, handleAuthResponse));
    } catch (error) {
      console.error('Login failed:', error);
      showErrorMessage(getUserReadableError(error) ?? 'Login failed');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode || !tempUserId) return;
    try {
      await dispatch(verifyTwoFactor({ userId: tempUserId, code: verificationCode }, navigate));
    } catch (error) {
      console.error('Verification failed:', error);
      showErrorMessage(getUserReadableError(error) ?? 'Invalid code');
    }
  };

  if (is2FA) {
    return (
      <div className="auth-screen">
        <div className="auth-logo-wrapper">
          <img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '24px',
        }}>
          <span style={{ fontSize: '28px' }}>🕶️</span>
          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            color: '#94a3b8',
          }}>
            Back Office
          </span>
          <span style={{ fontSize: '28px' }}>🕶️</span>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <ShieldCheck size={48} style={{ color: '#3b82f6' }} />
        </div>

        <div style={{
          textAlign: 'center',
          marginBottom: '24px',
          color: '#64748b',
          fontSize: '0.95rem',
          lineHeight: '1.5',
        }}>
          We sent a 6-digit code to your email.<br />Please enter it below to continue.
        </div>

        <div className="auth-stack">
          <form onSubmit={handleVerify2FA} className="auth-form">
            <div className="input-group">
              <label htmlFor="code" className="input-label">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                placeholder="123456"
                autoComplete="one-time-code"
                className="input-field"
                required
                autoFocus
              />
            </div>
            <div className="pt-2">
              <button type="submit" className="auth-button">Verify Device</button>
            </div>
          </form>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => { setIs2FA(false); setTempUserId(null); setVerificationCode(''); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-logo-wrapper">
        <img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '24px',
      }}>
        <span style={{ fontSize: '28px' }}>🕶️</span>
        <span style={{
          fontSize: '14px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '2px',
          color: '#94a3b8',
        }}>
          Back Office
        </span>
        <span style={{ fontSize: '28px' }}>🕶️</span>
      </div>
      <div className="auth-stack">
        <form onSubmit={(e) => handleSubmit(e)} className="auth-form">
          <div className="input-group">
            <label htmlFor="email" className="input-label">Email</label>
            <input
              type="email"
              id="email"
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              autoComplete="username"
              className="input-field"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="password" className="input-label">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                autoComplete="current-password"
                className="input-field"
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>
          <div className="pt-2">
            <button type="submit" className="auth-button">Sign in</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;